'use client';

import { includes as _includes } from 'lodash-es';
import { Fragment } from 'react';
import Link from 'next/link';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import HowToReg from '@mui/icons-material/HowToReg';
import MaterialLink from './material-link';
import MaterialLinkButton from './material-link-button';
import Host from './host';
import EventTime from './events/time';
import RsvpAttendeesSummary from './events/rsvp-attendees-summary';
import EventCategoryTags from './events/category-tags';
import LocationCompact from './events/location-compact';
import ImageUpload from './image-upload';
import EventListingActions from './events/event-listing-actions';
import EventHeaderTags from './events/header-tags';
import { eventPath } from '../utils/slugify';
import type { Event } from '../types/Event';
import type { Group } from '../types/Group';

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  imageCol: {
    flexShrink: 0,
    '&.no-image': {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  },
  detailsCol: {
    flexGrow: 1,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  detailsColWithImage: {
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(2),
    },
  },
  rsvpCol: {
    width: 160,
    flexShrink: 0,
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      marginTop: theme.spacing(1.5),
    },
  },
  imageLink: {
    width: 200,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    '& img': {
      display: 'block',
      width: 'auto',
      height: 'auto',
      maxWidth: '100%',
      maxHeight: 200,
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      '& img': {
        maxHeight: 200,
      },
    },
  },
  imagePlaceholder: {
    width: 200,
    height: 0,
  },
  eventTitle: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  eventTitleContent: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
    rowGap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  eventNameRow: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  eventName: {
    margin: theme.spacing(0, 1, 0, 0),
    fontSize: 18,
    overflowWrap: 'anywhere',
  },
  commentIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
  },
  headerTags: {
    maxWidth: 400,
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  },
  cancelled: {
    opacity: 0.65,
  },
  cancelledTitle: {
    textDecoration: 'line-through',
  },
  cancelledImage: {
    '& img': {
      maxHeight: 120,
    },
    [theme.breakpoints.down('md')]: {
      '& img': {
        maxHeight: 120,
      },
    },
  },
  host: {
    margin: '1px 0',
    fontSize: theme.typography.body2.fontSize,
    '& > *': {
      marginBottom: 2,
    },
  },
  iconRow: {
    display: 'flex',
    alignItems: 'flex-start',
    margin: theme.spacing(0.5, 0),
    fontSize: theme.typography.body2.fontSize,
  },
  rowIcon: {
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  locationHidden: {
    fontStyle: 'italic',
  },
  summary: {
    marginTop: 10,
    overflowWrap: 'anywhere',
  },
}));

type Props = {
  event?: Event;
  group?: Group;
  fullDate?: boolean;
};

const EventListing = ({ event, fullDate = false, group }: Props) => {
  const { classes, cx } = useStyles();
  const isCancelled = event.status === 'CANCELLED';

  // in case event is deleted but then viewing cached lists
  if (event.status === 'DELETED') {
    return null;
  }

  return (
    <div className={cx(classes.wrapper, { [classes.cancelled]: isCancelled })}>
      <div className={cx(classes.imageCol, { 'no-image': !event.image })}>
        <Link
          href={eventPath(event.id, event.name)}
          title={event.name}
          className={cx(classes.imageLink, { [classes.cancelledImage]: isCancelled })}
          >
          {event.image ? (
            <ImageUpload record={event} size={isCancelled ? '160x120iwe' : '400x600iwe'} />
          ) : (
            <div className={classes.imagePlaceholder} />
          )}
        </Link>
      </div>
      <div className={cx(classes.detailsCol, { [classes.detailsColWithImage]: !!event.image })}>
        <div className={classes.eventTitle}>
          <div className={classes.eventTitleContent}>
            <div className={classes.eventNameRow}>
              <Typography variant="h3" className={cx(classes.eventName, { [classes.cancelledTitle]: isCancelled })}>
                <MaterialLink href={eventPath(event.id, event.name)}>
                  {event.name}
                </MaterialLink>
              </Typography>
              {(event.commentCount + event.imageUploadsCount) > 0 && (
                <span className={classes.commentIndicator}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                  {event.commentCount + event.imageUploadsCount}
                </span>
              )}
            </div>
            <EventHeaderTags
              event={event}
              group={group}
              compact
              className={classes.headerTags}
            />
          </div>
          <Box sx={{ display: { xs: 'none', md: 'contents' } }}>
            <EventListingActions event={event} group={group} />
          </Box>
        </div>
        <div className={classes.iconRow}>
          <ScheduleIcon fontSize="small" className={classes.rowIcon} />
          <EventTime event={event} fullDate={fullDate} />
        </div>
        <div className={classes.iconRow}>
          <LocationOnIcon fontSize="small" className={classes.rowIcon} />
          {(event.registrationType === 'APPROVAL_REQUIRED' && !event.permissions.approved)
            ? <span className={classes.locationHidden}>Location hidden</span>
            : (
              event.eventType === 'ONLINE_EVENT'
                ? 'Online Event'
                : (
                  <LocationCompact
                    name={event.startLocation}
                    address={event.startLocationAddress}
                    details={event.startLocationDetails}
                  />
                )
            )
          }
        </div>
        <Host event={event} className={classes.host} />
        {!isCancelled && event.summary && (
          <p className={classes.summary}>
            {event.summary}
            {event.summary.substring(event.summary.length - 3) === '...' && (
              <Fragment>
                {' '}
                <MaterialLink href={eventPath(event.id, event.name)}>
                  read more
                </MaterialLink>
              </Fragment>
            )}
          </p>
        )}
        {!isCancelled && <EventCategoryTags event={event} />}
        {!isCancelled && (
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mt: 1, '& > *': { ml: 0 } }}>
            <EventListingActions event={event} group={group} />
          </Box>
        )}
      </div>
      <div className={classes.rsvpCol}>
        {!isCancelled && (
          <div>
            {event.permissions && !event.permissions.joinable && event.registrationType === 'APPROVAL_REQUIRED' &&
              (
                !event.membership ||
                !_includes(['GOING', 'INTERESTED', 'NOT_GOING'], event.membership.status)
              ) && (
              <div>
                <MaterialLinkButton
                  href={eventPath(event.id, event.name)}
                  variant="outlined"
                  color="primary"
                  fullWidth
                >
                  Request to join
                  {event.membership && event.membership.status === 'REQUESTED' && ' – Pending'}
                </MaterialLinkButton>
              </div>
            )}
            {event.permissions && event.permissions.joinable && (
              <RsvpAttendeesSummary event={event} />
            )}
            {event.registrationType === 'EXTERNAL_REGISTRATION' && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                <ConfirmationNumber sx={{ fontSize: 14, verticalAlign: 'text-top', position: 'relative', top: 1, mr: 0.5 }} />
                {event.infoUrl ? (
                  <MaterialLink href={event.infoUrl} target="_blank" rel="noopener noreferrer">
                    External registration required
                  </MaterialLink>
                ) : (
                  'External registration required'
                )}
              </Typography>
            )}
            {event.registrationType === 'APPROVAL_REQUIRED' && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                <HowToReg sx={{ fontSize: 14, verticalAlign: 'text-top', position: 'relative', top: 1, mr: 0.5 }} />
                Approval required
                {event.permissions?.editable && (event.memberSummary?.REQUESTED?.count ?? 0) > 0 && (
                  <> · <span style={{ color: 'var(--mui-palette-warning-main)' }}>{event.memberSummary.REQUESTED.count} pending</span></>
                )}
              </Typography>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventListing;
